import { useMemo, useState } from 'react';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { Badge, Button, Card, CardHeader, Checkbox } from '@/components/ui';
import { useToast } from '../ui/Toast';
import {
  useAgentCredentialGrants,
  useGrantableAgentCredentials,
  useSetAgentCredentialGrants,
} from '../../hooks/useAgentCredentials';
import { useProviderCatalog } from '../../hooks/useIdentityCredentials';
import {
  credentialFamily,
  type AgentCredentialGrant,
  type CredentialOwnerKind,
  type GrantableCredential,
} from '../../types/agent-credentials';

/**
 * Agent → credential assignments. Once an agent holds any credential for a
 * provider, optr-identity lets it use ONLY those for that provider (strict
 * allow-list), so tool calls and downstream audit logs run as this agent.
 */

type CredentialLike = Pick<
  GrantableCredential,
  'credId' | 'provider' | 'family' | 'providerInstanceId' | 'displayName' | 'details' | 'ownerKind'
>;

const OWNER_LABEL: Record<CredentialOwnerKind, string> = {
  'space-service': 'Space',
  'org-service-account': 'Org',
  user: 'Personal',
};

/** Human label: AWS roles as `accountName / accountId — roleName`, else the display name. */
export function credentialLabel(c: CredentialLike): string {
  const d = c.details ?? {};
  if (d.accountId && d.roleName) {
    const account = d.accountName ? `${d.accountName} / ${d.accountId}` : d.accountId;
    return `${account} — ${d.roleName}`;
  }
  return c.displayName ?? c.providerInstanceId ?? c.credId;
}

interface Selection {
  selected: Set<string>;
  /** family → default credId */
  defaults: Map<string, string>;
}

function selectionFromGrants(grants: AgentCredentialGrant[]): Selection {
  return {
    selected: new Set(grants.map((g) => g.credId)),
    defaults: new Map(
      grants.filter((g) => g.isDefault).map((g) => [credentialFamily(g), g.credId]),
    ),
  };
}

function groupByFamily<T extends { provider: string; family?: string }>(items: T[]): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const family = credentialFamily(item);
    groups.set(family, [...(groups.get(family) ?? []), item]);
  }
  return new Map([...groups.entries()].sort(([a], [b]) => a.localeCompare(b)));
}

interface AgentCredentialsTabProps {
  agentId: string;
  canEdit: boolean;
}

export default function AgentCredentialsTab({ agentId, canEdit }: AgentCredentialsTabProps) {
  const { success, error: showError } = useToast();
  const { data: grants = [], isLoading, error } = useAgentCredentialGrants(agentId);
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState<Selection>(() => selectionFromGrants([]));
  const grantable = useGrantableAgentCredentials(agentId, editing);
  const setGrants = useSetAgentCredentialGrants(agentId);
  const { data: catalog } = useProviderCatalog();

  const providerName = useMemo(() => {
    const names = new Map((catalog?.providers ?? []).map((p) => [p.id, p.displayName]));
    return (family: string) => (family === 'aws' ? 'AWS' : names.get(family) ?? family);
  }, [catalog]);

  // Picker options: grantable credentials plus currently granted ones that are
  // no longer grantable (e.g. credential moved/revoked) so they can be removed.
  const options = useMemo<CredentialLike[]>(() => {
    const byId = new Map<string, CredentialLike>((grantable.data ?? []).map((c) => [c.credId, c]));
    for (const g of grants) if (!byId.has(g.credId)) byId.set(g.credId, g);
    return [...byId.values()];
  }, [grantable.data, grants]);
  const grantableIds = useMemo(
    () => new Set((grantable.data ?? []).map((c) => c.credId)),
    [grantable.data],
  );

  const startEditing = () => {
    setSelection(selectionFromGrants(grants));
    setEditing(true);
  };

  const toggle = (c: CredentialLike, checked: boolean) => {
    setSelection((prev) => {
      const selected = new Set(prev.selected);
      const defaults = new Map(prev.defaults);
      const family = credentialFamily(c);
      if (checked) selected.add(c.credId);
      else {
        selected.delete(c.credId);
        if (defaults.get(family) === c.credId) defaults.delete(family);
      }
      return { selected, defaults };
    });
  };

  const setDefault = (family: string, credId: string) => {
    setSelection((prev) => ({ ...prev, defaults: new Map(prev.defaults).set(family, credId) }));
  };

  const handleSave = async () => {
    const byId = new Map(options.map((c) => [c.credId, c]));
    const input = [...selection.selected].map((credId) => {
      const c = byId.get(credId);
      const family = c ? credentialFamily(c) : undefined;
      const familyCount = options.filter(
        (o) => selection.selected.has(o.credId) && credentialFamily(o) === family,
      ).length;
      // A default only matters when the family has several grants.
      const isDefault = familyCount > 1 && family !== undefined && selection.defaults.get(family) === credId;
      return { credId, ...(isDefault && { isDefault: true }) };
    });
    try {
      await setGrants.mutateAsync(input);
      success('Agent credentials updated');
      setEditing(false);
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update agent credentials');
    }
  };

  const grantedGroups = groupByFamily(grants);
  const optionGroups = groupByFamily(options);

  return (
    <Card>
      <CardHeader
        title="Credentials"
        action={
          canEdit && !editing ? (
            <Button variant="secondary" size="sm" onClick={startEditing}>
              Manage Credentials
            </Button>
          ) : undefined
        }
      />

      <div className="mb-4 flex gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Assign the credentials this agent acts with. Once a credential is assigned for a
          provider (for example an AWS IAM role), the agent can use{' '}
          <span className="font-medium text-foreground">only</span> its assigned credentials for
          that provider, and calls are audited as this agent. Other providers keep the normal
          lookup. Changes apply immediately to every version.
        </p>
      </div>

      {!editing &&
        (isLoading ? (
          <p className="text-muted-foreground">Loading credentials…</p>
        ) : error ? (
          <p className="text-red-600">Failed to load credentials</p>
        ) : grants.length === 0 ? (
          <p className="text-muted-foreground" data-testid="agent-credentials-empty">
            No credentials assigned. This agent uses the normal lookup (your personal
            connections, then space and org credentials).
          </p>
        ) : (
          <div className="space-y-4" data-testid="agent-credentials-list">
            {[...grantedGroups.entries()].map(([family, items]) => (
              <div key={family}>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {providerName(family)}
                </h4>
                <div className="divide-y divide-border">
                  {items.map((g) => (
                    <div key={g.credId} className="flex items-center justify-between gap-3 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <KeyRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate font-medium text-foreground">{credentialLabel(g)}</span>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {g.isDefault && <Badge variant="info">Default</Badge>}
                        <Badge variant="gray">{OWNER_LABEL[g.ownerKind] ?? g.ownerKind}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

      {editing && (
        <div className="space-y-4" data-testid="agent-credentials-editor">
          {grantable.isLoading ? (
            <p className="text-muted-foreground">Loading available credentials…</p>
          ) : grantable.error ? (
            <p className="text-red-600">Failed to load available credentials</p>
          ) : options.length === 0 ? (
            <p className="text-muted-foreground">
              No credentials are available in this space or org yet. Add them under Credentials first.
            </p>
          ) : (
            [...optionGroups.entries()].map(([family, items]) => {
              const selectedInFamily = items.filter((c) => selection.selected.has(c.credId));
              const needsDefault = selectedInFamily.length > 1;
              return (
                <fieldset key={family} className="rounded-md border border-border p-3">
                  <legend className="px-1 text-sm font-semibold text-foreground">
                    {providerName(family)}
                  </legend>
                  {selectedInFamily.length > 0 && (
                    <p className="mb-2 text-xs text-muted-foreground">
                      This agent will only be able to use the selected {providerName(family)} credentials.
                      {needsDefault && ' Pick a default for calls that don’t name one.'}
                    </p>
                  )}
                  <div className="space-y-2">
                    {items.map((c) => {
                      const checked = selection.selected.has(c.credId);
                      const unavailable = !grantableIds.has(c.credId);
                      const id = `grant-${c.credId}`;
                      return (
                        <div key={c.credId} className="flex items-center justify-between gap-3">
                          <label htmlFor={id} className="flex min-w-0 cursor-pointer items-center gap-2 text-sm">
                            <Checkbox
                              id={id}
                              checked={checked}
                              onCheckedChange={(v) => toggle(c, v === true)}
                              aria-label={credentialLabel(c)}
                            />
                            <span className="truncate">{credentialLabel(c)}</span>
                            <Badge variant="gray">{OWNER_LABEL[c.ownerKind] ?? c.ownerKind}</Badge>
                            {unavailable && <Badge variant="warning">No longer available</Badge>}
                          </label>
                          {needsDefault && checked && (
                            <label className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                              <input
                                type="radio"
                                name={`default-${family}`}
                                checked={selection.defaults.get(family) === c.credId}
                                onChange={() => setDefault(family, c.credId)}
                                aria-label={`Default ${providerName(family)}: ${credentialLabel(c)}`}
                              />
                              Default
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              );
            })
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={setGrants.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={setGrants.isPending || grantable.isLoading}>
              {setGrants.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
